import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BuildingAPI, FloorAPI, resolveAsset } from "../../../services/api";
import { ReactComponent as CheckIcon } from "../../../assets/icons/addbuilding/check.svg";
import PageHeader from "../../../components/addbuilding/PageHeader";
import FileUploadArea from "../../../components/addbuilding/FileUploadArea";
import FormField from "../../../components/addbuilding/FormField";
import FloorSelector from "../../../components/addbuilding/FloorSelector";
import ActionButtons from "../../../components/addbuilding/ActionButtons";
import MapBackground from "../../../components/addbuilding/MapBackground";
import BuildingLocationPicker from "../../../components/addbuilding/BuildingLocationPicker";

export default function AddBuilding({ isEditMode: propEditMode = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const buildingId = searchParams.get("buildingId");
  const isEditMode = propEditMode || !!buildingId;

  // ===== State: building info form =====
  const [buildingName, setBuildingName] = useState("");
  const [buildingAddress, setBuildingAddress] = useState("");
  const [buildingImage, setBuildingImage] = useState(null);
  const [buildingImagePreview, setBuildingImagePreview] = useState(null);
  const [buildingLocation, setBuildingLocation] = useState(null);
  const [activeFloorId, setActiveFloorId] = useState(1);

  // ===== State: floor list (dynamic) =====
  const [floorItems, setFloorItems] = useState(() => [
    { id: 1, title: t("admin.addBuilding.defaultBuildingName"), floor: t("admin.addBuilding.floorNum", { num: 1 }), level: 1, desc: "" },
  ]);

  // ===== State: per-floor data (image + name) =====
  const [floorData, setFloorData] = useState({
    1: { image: null, name: "", level: 1 },
  });

  // ===== State: API response results =====
  const [createdBuildingId, setCreatedBuildingId] = useState(null);
  const [lastCreatedFloorId, setLastCreatedFloorId] = useState(null);

  // ===== State: UI flags =====
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [openModal, setOpenModal] = useState(false);

  // ===== Derived: active floor's data =====
  const activeFloorData = floorData[activeFloorId] || { image: null, name: "", level: 1 };
  const activeFloorLevel = activeFloorData.level ?? (typeof activeFloorId === "number" ? activeFloorId : 1);

  // Load existing building + floors in Edit Mode
  useEffect(() => {
    if (!buildingId) return;

    // Load building details
    BuildingAPI.get(buildingId)
      .then((b) => {
        setBuildingName(b.name || "");
        setBuildingAddress(b.address || "");
        if (b.imageUrl || b.img) {
          setBuildingImagePreview(resolveAsset(b.imageUrl || b.img));
        }
        if (b.lat && b.lng) {
          setBuildingLocation({ lat: Number(b.lat), lng: Number(b.lng) });
        }
      })
      .catch((err) => console.error("Failed to load building:", err));

    // Load building floors
    FloorAPI.listByBuilding(buildingId)
      .then((floors) => {
        const list = Array.isArray(floors) ? floors : floors?.items || [];
        const sorted = [...list].sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
        if (sorted.length > 0) {
          const items = sorted.map(f => ({
            id: f.id,
            level: f.level ?? 1,
            title: buildingName,
            floor: f.name || t("admin.addBuilding.floorNum", { num: f.level }),
            desc: ""
          }));
          setFloorItems(items);

          const fd = {};
          sorted.forEach(f => {
            fd[f.id] = {
              id: f.id,
              name: f.name || "",
              level: f.level ?? 1,
              imageUrl: f.imageUrl || f.mapImageUrl || "",
              image: null
            };
          });
          setFloorData(fd);
          setActiveFloorId(sorted[0].id);
        }
      })
      .catch((err) => console.error("Failed to load floors:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, t]);

  // Sync floor items building title when buildingName updates
  useEffect(() => {
    setFloorItems(prev =>
      prev.map(item => ({ ...item, title: buildingName }))
    );
  }, [buildingName]);

  // Fetch specific floor details when activeFloorId changes
  useEffect(() => {
    if (!activeFloorId || String(activeFloorId).startsWith("new-")) return;

    FloorAPI.get(activeFloorId)
      .then((f) => {
        setFloorData(prev => ({
          ...prev,
          [f.id]: {
            ...prev[f.id],
            name: f.name || "",
            level: f.level ?? 1,
            imageUrl: f.imageUrl || f.mapImageUrl || "",
          }
        }));
      })
      .catch((err) => console.error("Failed to load floor details:", err));
  }, [activeFloorId]);

  useEffect(() => {
    if (!buildingImage) {
      if (!isEditMode) {
        setBuildingImagePreview(null);
      }
      return undefined;
    }

    const previewUrl = URL.createObjectURL(buildingImage);
    setBuildingImagePreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [buildingImage, isEditMode]);

  // ===== Labels that change based on mode =====
  const pageTitle = isEditMode ? t("admin.addBuilding.editMapTitle") : t("admin.addBuilding.specifyMapTitle");
  const confirmLabel = isEditMode ? t("admin.addBuilding.editSave") : saving ? t("common.saving") : t("common.save");
  const confirmVariant = isEditMode ? "yellow" : "blue";

  // ===== Handler: add new floor =====
  const handleAddFloor = () => {
    const tempId = `new-${Date.now()}`;
    const nextLevel = floorItems.length > 0 
      ? Math.max(...floorItems.map(item => Number(item.level ?? item.id ?? 0))) + 1
      : 1;

    setFloorItems(prev => [
      ...prev,
      { 
        id: tempId, 
        title: buildingName, 
        floor: t("admin.addBuilding.floorNum", { num: nextLevel }), 
        level: nextLevel,
        desc: "ร่าง" 
      },
    ]);
    setFloorData(prev => ({
      ...prev,
      [tempId]: { image: null, name: t("admin.addBuilding.floorNum", { num: nextLevel }).replace("- ", ""), level: nextLevel },
    }));
    setActiveFloorId(tempId);
  };

  const handleFloorNameChange = (e) => {
    const newName = e.target.value;
    setFloorData(prev => ({
      ...prev,
      [activeFloorId]: { ...prev[activeFloorId], name: newName }
    }));
    setFloorItems(prev =>
      prev.map(item =>
        item.id === activeFloorId ? { ...item, floor: newName } : item
      )
    );
  };

  const handleFloorLevelChange = (e) => {
    const newLevel = Number(e.target.value) || 1;
    setFloorData(prev => ({
      ...prev,
      [activeFloorId]: { ...prev[activeFloorId], level: newLevel }
    }));
    setFloorItems(prev =>
      prev.map(item =>
        item.id === activeFloorId ? { ...item, level: newLevel } : item
      )
    );
  };

  const handleDeleteFloor = () => {
    if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบชั้นนี้? ทุกแผนที่และหมุดของชั้นนี้จะถูกลบออกด้วย")) {
      return;
    }

    if (String(activeFloorId).startsWith("new-")) {
      const remaining = floorItems.filter(item => item.id !== activeFloorId);
      setFloorItems(remaining);
      setFloorData(prev => {
        const copy = { ...prev };
        delete copy[activeFloorId];
        return copy;
      });
      if (remaining.length > 0) {
        setActiveFloorId(remaining[0].id);
      }
    } else {
      setSaving(true);
      FloorAPI.remove(activeFloorId)
        .then(() => {
          const remaining = floorItems.filter(item => item.id !== activeFloorId);
          setFloorItems(remaining);
          setFloorData(prev => {
            const copy = { ...prev };
            delete copy[activeFloorId];
            return copy;
          });
          if (remaining.length > 0) {
            setActiveFloorId(remaining[0].id);
          }
          alert("ลบชั้นเรียบร้อยแล้ว");
        })
        .catch(err => {
          alert(err.message || "ลบชั้นไม่สำเร็จ");
        })
        .finally(() => {
          setSaving(false);
        });
    }
  };

  // ===== Handler: update image for active floor =====
  const handleFloorImageChange = (file) => {
    setFloorData(prev => ({
      ...prev,
      [activeFloorId]: { ...prev[activeFloorId], image: file },
    }));
  };

  // ===== Handler: save building + ALL floors =====
  const handleSave = async () => {
    setErrMsg("");
    setSaving(true);
    try {
      if (isEditMode) {
        // 1. Update building details
        await BuildingAPI.update(buildingId, {
          name: buildingName,
          address: buildingAddress,
          image: buildingImage || undefined,
          lat: buildingLocation?.lat,
          lng: buildingLocation?.lng,
        });

        // 2. Loop and save floors
        let lastId = null;
        for (const item of floorItems) {
          const fd = floorData[item.id];
          if (String(item.id).startsWith("new-")) {
            // Create new floor
            const newF = await FloorAPI.create(buildingId, {
              level: fd.level,
              name: fd.name || t("admin.addBuilding.floorNum", { num: fd.level }).replace("- ", ""),
              image: fd.image || null,
            });
            lastId = newF.id;
          } else {
            // Update existing floor details/image
            const payload = {
              level: fd.level,
              name: fd.name || t("admin.addBuilding.floorNum", { num: fd.level }).replace("- ", ""),
            };
            if (fd.image) {
              payload.image = fd.image;
            }
            await FloorAPI.update(item.id, payload);
            lastId = item.id;
          }
        }
        setCreatedBuildingId(buildingId);
        setLastCreatedFloorId(lastId);
        setOpenModal(true);
      } else {
        // Create mode (original flow)
        if (!buildingName) {
          setErrMsg(t("admin.addBuilding.requiredBuildingName"));
          setSaving(false);
          return;
        }

        const building = await BuildingAPI.create({
          name: buildingName,
          address: buildingAddress,
          image: buildingImage,
          lat: buildingLocation?.lat,
          lng: buildingLocation?.lng,
        });
        setCreatedBuildingId(building?.id || null);

        let lastFloorId = null;
        for (const item of floorItems) {
          const fd = floorData[item.id];
          const floor = await FloorAPI.create(building.id, {
            level: fd?.level || item.id,
            name: fd?.name || t("admin.addBuilding.floorNum", { num: fd?.level || item.id }).replace("- ", ""),
            image: fd?.image || null,
          });
          lastFloorId = floor?.id || null;
        }
        setLastCreatedFloorId(lastFloorId);
        setOpenModal(true);
      }
    } catch (e) {
      setErrMsg(e.message || t("admin.addBuilding.savingError"));
    } finally {
      setSaving(false);
    }
  };

  // ===== Handler: navigate to map editor =====
  const handleGoToMapEditor = () => {
    setOpenModal(false);
    const qs = lastCreatedFloorId
      ? `?floorId=${lastCreatedFloorId}`
      : createdBuildingId
        ? `?buildingId=${createdBuildingId}`
        : "";
    navigate(`/admin2${qs}`);
  };

  return (
    <div className="font-sans">
      {/* ===== Page header ===== */}
      <PageHeader
        title={pageTitle}
        onBack={() => navigate("/admin/listallbuilding")}
        titleClassName="text-xl font-semibold"
      />

      {/* ===== Layout: FloorSelector | form fields ===== */}
      <div className="flex gap-4">

        {/* ---- Floor selector ---- */}
        <FloorSelector
          items={floorItems}
          activeId={activeFloorId}
          onSelect={setActiveFloorId}
          onAddFloor={handleAddFloor}
        />

        {/* ---- Data form + image preview ---- */}
        <div className="flex flex-col flex-1">

          {/* Building settings card */}
          <div className="border bg-white rounded-lg p-6">
            <div className="font-semibold text-slate-900">
              {t("admin.addBuilding.specifyBuildingInfo")}
              <div className="border-b-2 border-slate-200 pb-2 mb-4" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[260px_1fr] gap-5">
              <div>
                <FileUploadArea
                  accept=".jpg,.jpeg,.png"
                  fileTypes="JPEG / PNG"
                  selectedFile={
                    buildingImage ||
                    (buildingImagePreview && !buildingImage
                      ? { name: buildingImagePreview.split("/").pop() || "existing-image.png" }
                      : null)
                  }
                  onChange={setBuildingImage}
                  className="m-0 h-full"
                />
              </div>

              <div className="min-w-0">
                <div
                  className="h-[240px] rounded-xl border border-slate-200 bg-slate-50 bg-cover bg-center"
                  style={{
                    backgroundImage: buildingImagePreview
                      ? `url(${buildingImagePreview})`
                      : undefined,
                  }}
                >
                  {!buildingImagePreview && (
                    <div className="flex h-full items-center justify-center text-sm font-semibold text-slate-400">
                      {t("admin.addBuilding.buildingImageLabel")}
                    </div>
                  )}
                </div>

                <div className="mt-4 grid gap-3">
                  <FormField
                    label={t("admin.addBuilding.buildingNameLabel")}
                    required
                    placeholder={t("admin.addBuilding.enterBuildingName")}
                    value={buildingName}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setBuildingName(newName);
                      setFloorItems(prev =>
                        prev.map(item => ({ ...item, title: newName }))
                      );
                    }}
                  />
                  <FormField
                    label={t("admin.addBuilding.buildingAddressLabel")}
                    placeholder={t("admin.addBuilding.enterAddress")}
                    value={buildingAddress}
                    onChange={(e) => setBuildingAddress(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => navigate("/admin/listallbuilding")}
                className="mr-2 px-6 py-2 bg-white text-black rounded-lg border border-gray-200 hover:bg-gray-50 transition animate-fade-in"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-9 py-2 bg-blue-50 text-blue-600 border border-blue-600 rounded-lg disabled:opacity-60 hover:bg-blue-100 transition shadow-sm font-semibold"
              >
                {saving ? t("common.saving") : t("admin.addBuilding.saveFloorInfo")}
              </button>
            </div>
          </div>

          {/* Location picker */}
          <div className="border bg-white rounded-lg p-6 mt-2">
            <div className="font-semibold text-slate-900">
              {t("admin.addBuilding.buildingLocationLabel")}
              <div className="border-b-2 border-slate-200 pb-2 mb-4" />
            </div>
            <BuildingLocationPicker
              value={buildingLocation}
              onChange={setBuildingLocation}
              showLabel={false}
            />
          </div>

          {/* ===== Floor plan image section ===== */}
          {!activeFloorId ? (
            <div className="border bg-white rounded-lg p-6 mt-2 flex flex-col items-center justify-center h-[200px]">
              <p className="text-slate-500 font-semibold mb-4">
                ยังไม่มีข้อมูลชั้นสำหรับอาคารนี้
              </p>
              <button
                type="button"
                onClick={handleAddFloor}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition cursor-pointer"
              >
                {t("admin.addBuilding.addInternalFloor") || "+ เพิ่มจำนวนชั้นภายใน"}
              </button>
            </div>
          ) : (
            <div className="border bg-white rounded-lg p-6 mt-2">
              <div className="flex justify-between items-center pb-2 border-b-2 border-slate-200 mb-4">
                <div className="font-semibold text-slate-900 text-lg">
                  {t("admin.addBuilding.floorMapImageTitle", { floor: activeFloorLevel })}
                </div>
                {floorItems.length > 1 && (
                  <button
                    type="button"
                    onClick={handleDeleteFloor}
                    className="px-4 py-1.5 text-sm font-semibold bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 active:bg-red-200 transition duration-150 flex items-center gap-1 cursor-pointer"
                  >
                    <span>{t("common.delete")}</span>
                  </button>
                )}
              </div>

              {/* Inputs to edit Floor Level and Floor Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <FormField
                  label="เลขชั้น / Floor Level"
                  required
                  type="number"
                  placeholder="เช่น 1"
                  value={activeFloorLevel}
                  onChange={handleFloorLevelChange}
                />
                <FormField
                  label="ชื่อชั้น / Floor Name"
                  required
                  placeholder="เช่น ชั้นที่ 1"
                  value={activeFloorData.name || ""}
                  onChange={handleFloorNameChange}
                />
              </div>

              <div className="flex gap-4 h-[360px]">
                <FileUploadArea
                  accept=".jpg,.jpeg,.png"
                  fileTypes="JPEG / PNG"
                  selectedFile={
                    activeFloorData.image ||
                    (activeFloorData.imageUrl
                      ? { name: activeFloorData.imageUrl.split("/").pop() || "floor-map.png" }
                      : null)
                  }
                  onChange={handleFloorImageChange}
                />
                <div className="flex-1">
                  <MapBackground
                    imageUrl={
                      activeFloorData.image
                        ? URL.createObjectURL(activeFloorData.image)
                        : activeFloorData.imageUrl
                          ? resolveAsset(activeFloorData.imageUrl)
                          : null
                    }
                    className="relative border border-slate-200 rounded-2xl h-[360px] mb-16"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ===== Bottom action buttons ===== */}
          <ActionButtons
            onCancel={() => navigate("/admin/listallbuilding")}
            onConfirm={handleSave}
            confirmLabel={confirmLabel}
            confirmVariant={confirmVariant}
            confirmDisabled={saving}
            errorMessage={errMsg}
          />
        </div>
      </div>

      {/* ===== Success modal ===== */}
      {openModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80">
          <div className="relative z-[10000] bg-white rounded-lg p-3 w-[380px] h-[420px] shadow-lg">

            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mt-4">
              <CheckIcon className="w-10 h-10 text-green-400" />
            </div>

            <div className="text-lg text-center font-semibold mb-2 mt-7">
              {isEditMode ? t("admin.addBuilding.editSuccess") : t("admin.addBuilding.createSuccess")}
            </div>
            <div className="text-green-600 text-[12px] text-center mb-1">
              {t("admin.addBuilding.newBuildingAdded")}
            </div>

            <div className="border border-green-400 bg-green-50 rounded-md mt-2 p-6">
              <div className="flex items-center justify-between w-full">
                <div className="flex-1">
                  <p className="text-[12px] text-green-600">{t("admin.addBuilding.buildingNameLabel")}</p>
                  <p className="text-[14px]">{buildingName}</p>
                </div>
                <div className="w-px h-12 bg-green-600 mx-6 shrink-0" />
                <div className="shrink-0 text-right">
                  <p className="text-[12px] text-green-600">{t("admin.addBuilding.floorsCountValHeader")}</p>
                  {/* ✅ แสดงจำนวนชั้นจริงจาก state */}
                  <p className="text-[14px] mt-1">{t("admin.addBuilding.floorsCountVal", { count: floorItems.length })}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-center w-full mt-8">
              <button
                onClick={() => { setOpenModal(false); navigate("/admin/listallbuilding"); }}
                className="flex items-center justify-center text-xs font-bold w-full py-3
                  bg-gray-50 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition mr-2"
              >
                {t("admin.addBuilding.viewBuildingDetails")}
              </button>
              <button
                onClick={handleGoToMapEditor}
                className="flex items-center justify-center text-xs font-bold w-full py-3
                  bg-blue-600 border border-gray-300 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t("admin.addBuilding.goToMapEditor")}
              </button>
            </div>

            <div className="flex justify-center items-center gap-2 text-green-600 mt-4">
              <button onClick={() => setOpenModal(false)} className="rounded-lg text-xl hover:opacity-70">
                ‹
              </button>
              <button onClick={() => setOpenModal(false)} className="text-xs mt-0.5 text-green-600 hover:underline">
                {t("admin.addBuilding.backToCreateBuilding")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
